import Secure from "@/secure/payload";
import { C_API } from "@/utils/helper/api";
import { useQuery } from "@tanstack/react-query";
import { PaymentApiResponse, PaymentApiParams, PaymentTransaction, PaymentStatsResponse, ExportHistoryApiResponse, ExportHistoryApiParams } from "@/types/payment";
import { API_URLS } from "@/utils/constant/api.constant";
import { buildApiUrl, handleApiResponse, handleApiError } from "@/utils/api-helpers";

export const fetchPayments = async (payload: PaymentApiParams): Promise<PaymentApiResponse> => {  
  // Define the parameters that should be sent to the API
  const apiParams = {
    page: payload.page,
    per_page: payload.per_page,
    search: payload.search,
    status: payload.status,
    from_date: payload.from_date,
    to_date: payload.to_date,
    emp_id: payload.emp_id,
    filters: payload.filters, // Add filters to API params
    sortBy: payload.sortBy, // Add sortBy to API params
    tab: payload.tab, // Add tab to API params
  };
  
  const url = buildApiUrl(API_URLS.PAYMENT.HISTORY, apiParams);
  
  try {
    const response = await C_API.get(url);
    return handleApiResponse(response);
  } catch (error) {
    handleApiError(error, 'fetchPayments');
    throw error; // Re-throw to maintain the error flow
  }
};

export const fetchPaymentSummary = async (tab: "incoming" | "outgoing"): Promise<any> => {
  // For now, we'll get summary from the same endpoint with different parameters
  // You can create a separate summary endpoint later if needed
  const payload = { tab, page: 1, pageSize: 1, summaryOnly: true };
  const encryptData = Secure.payloadEncryption(payload);
  const response = await C_API.get(API_URLS.PAYMENT.HISTORY, encryptData);
  return response.data?.data?.summary;
};

export const fetchPaymentDetails = async (transactionId: string): Promise<PaymentTransaction> => {
  try {
    const encryptData = Secure.payloadEncryption({});
    const response = await C_API.get(`${API_URLS.PAYMENT.TRANSACTION_DETAILS}/${transactionId}`, encryptData);
    return handleApiResponse(response);
  } catch (error) {
    handleApiError(error, 'fetchPaymentDetails');
    throw error; // Re-throw to maintain the error flow
  }
};

export const downloadPaymentInvoice = async (transactionId: string): Promise<{ invoiceUrl: string }> => {
  try {
    const encryptData = Secure.payloadEncryption({ transactionId });
    const response = await C_API.get(`${API_URLS.PAYMENT.DOWNLOAD_INVOICE}/${transactionId}`, encryptData);
    return handleApiResponse(response);
  } catch (error) {
    handleApiError(error, 'downloadPaymentInvoice');
    throw error; // Re-throw to maintain the error flow
  }
};

export const fetchPaymentStats = async (): Promise<PaymentStatsResponse> => {
  try {
    const response = await C_API.get(API_URLS.PAYMENT.STATS);
    return response.data?.data;
  } catch (error) {
    handleApiError(error, 'fetchPaymentStats');
    throw error; // Re-throw to maintain the error flow
  }
};

export const usePayments = (payload: PaymentApiParams) => {
  const { enabled = false, ...rest } = payload || {};
  return useQuery({
    queryKey: ["payments", rest],
    queryFn: () => fetchPayments(rest),
    staleTime: 0, // Set to 0 to always refetch when manually triggered
    enabled,
    retry: false,
    refetchOnWindowFocus: false, // Prevent automatic refetch on window focus
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnReconnect: true, // Refetch when network reconnects
  });
};

export const usePaymentSummary = (tab: "incoming" | "outgoing") => {
  return useQuery({
    queryKey: ["paymentSummary", tab],
    queryFn: () => fetchPaymentSummary(tab),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

export const usePaymentDetails = (transactionId: string) => {
  return useQuery({
    queryKey: ["paymentDetails", transactionId],
    queryFn: () => fetchPaymentDetails(transactionId),
    enabled: !!transactionId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

export const usePaymentStats = () => {
  return useQuery({
    queryKey: ["paymentStats"],
    queryFn: fetchPaymentStats,
    staleTime: 0, // Set to 0 to always refetch when manually triggered
    retry: false,
    refetchOnWindowFocus: false, // Prevent automatic refetch on window focus
    refetchOnMount: true, // Always refetch when component mounts
    refetchOnReconnect: true, // Refetch when network reconnects
  });
};

// Export History Services
export const fetchExportHistory = async (payload: ExportHistoryApiParams): Promise<ExportHistoryApiResponse> => {
  const apiParams = {
    page: payload.page,
    per_page: payload.per_page,
    search: payload.search,
    status: payload.status,
    from_date: payload.from_date,
    to_date: payload.to_date,
    sortBy: payload.sortBy,
  };
  
  const url = buildApiUrl(API_URLS.PAYMENT.EXPORT_HISTORY, apiParams);
  
  try {
    const response = await C_API.get(url);
    return handleApiResponse(response);
  } catch (error) {
    handleApiError(error, 'fetchExportHistory');
    throw error;
  }
};

export const useExportHistory = (payload: ExportHistoryApiParams) => {
  const { enabled = false, ...rest } = payload || {};
  return useQuery({
    queryKey: ["exportHistory", rest],
    queryFn: () => fetchExportHistory(rest),
    staleTime: 0,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
};


export const exportTransactions = async (transactionIds: string[], mode: string): Promise<any> => {
  try {
    const payload = {
      transactionIds,
      mode
    };
    const response = await C_API.post(API_URLS.PAYMENT.EXPORT_TRANSACTIONS, payload);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};